import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useMediaQuery } from 'react-responsive';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import Select from 'react-select';

import { ApiObject, Campus, School, Program } from 'app/types';

import Text from 'common/components/Text/Text';

import styles from 'features/explore/school/SchoolDetail.module.scss';
import { useNavigate } from 'react-router-dom';
import { detailUrl } from 'common/utils/conversions';
import { checkOverflow, entityIsProgram, entityIsSchool } from 'common/utils';

interface CompProps {
  entity?: ApiObject<School> | ApiObject<Program>;
  studentLifeRef: React.RefObject<HTMLDivElement>;
}

interface CampusCardProps {
  campus?: Campus;
}

const CampusCard = ({ campus }: CampusCardProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <div className={styles.campusCardContainer}>
      <img
        alt={`Photo of ${campus?.name}`}
        className={styles.professorPhoto}
        src={
          campus?.latitude && campus.longitude
            ? `https://maps.googleapis.com/maps/api/staticmap?markers=${campus.latitude},${campus.longitude}&\
            zoom=14&size=745x345&key=AIzaSyCSHHhzKxa8ch6Z8cm-jrtRXq7CXVLvD6w`
            : ''
        }
      />
      <Text
        size={isMobile ? 14 : 16}
        weight={600}
        inlineStyle={{ marginBottom: '8px', marginTop: '16px' }}
      >
        {campus?.name}
      </Text>
      <Text size={isMobile ? 14 : 16} weight={400}>
        {campus?.description}
      </Text>
    </div>
  );
};
const StudentLife = ({ entity, studentLifeRef }: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [campusSelected, setCampus] = useState(
    entityIsSchool(entity?.data)
      ? entity?.data.campuses && entity?.data.campuses.length > 0
        ? {
            label: entity?.data.campuses[0].name,
            value: entity?.data.campuses[0].id,
          }
        : {}
      : entity?.data.school.campuses && entity?.data.school.campuses.length > 0
      ? {
          label: entity?.data.school.campuses[0].name,
          value: entity?.data.school.campuses[0].id,
        }
      : {}
  );
  const [overflowCampusActive, setOverflowCampusActive] = useState<boolean>(false);
  const [overflowAcommodationsActive, setOverflowAcommodationsActive] = useState<boolean>(false);
  const [overflowLifePhotosActive, setOverflowLifePhotosActive] = useState<boolean>(false);
  const refLocations = useRef<any>();
  const refAcommodations = useRef<any>();
  const refLifePhotos = useRef<any>();
  const navigate = useNavigate();

  const handleMap = (schoolName?: string) => {
    if (schoolName == 'University of Law') {
      return 'https://maps.googleapis.com/maps/api/staticmap?markers=University%20of%20Law,Bunhill%20Row&zoom=14&size=745x345&key=AIzaSyCSHHhzKxa8ch6Z8cm-jrtRXq7CXVLvD6w';
    } else {
      return `https://maps.googleapis.com/maps/api/staticmap?markers=${schoolName}&zoom=14&size=745x345&key=AIzaSyCSHHhzKxa8ch6Z8cm-jrtRXq7CXVLvD6w`;
    }
  };

  const handleCountryDetailButton = () => {
    navigate(
      detailUrl(
        'countries',
        campusFound?.city.country.id
          ? campusFound?.city.country.id
          : entityIsSchool(entity?.data)
          ? entity?.data.city.country.id
          : entity?.data.school.city.country.id,
        campusFound?.city.country.name
          ? campusFound?.city.country.name
          : entityIsSchool(entity?.data)
          ? entity?.data.city.country.name
          : entity?.data.school.city.country.name
      )
    );
  };

  const [campusFound, setCampusFound] = useState<Campus | undefined>(
    entityIsSchool(entity?.data)
      ? entity?.data.campuses.find((c: Campus) => c.id === campusSelected.value)
      : entity?.data.school.campuses.find((c: Campus) => c.id === campusSelected.value)
  );

  useEffect(() => {
    setCampusFound(
      entityIsSchool(entity?.data)
        ? entity?.data.campuses.find((c: Campus) => c.id === campusSelected.value)
        : entity?.data.school.campuses.find((c: Campus) => c.id === campusSelected.value)
    );
  }, [campusSelected]);

  const scroll = (scrollOffset: number, reference: React.RefObject<HTMLDivElement>) => {
    reference.current ? (reference.current.scrollLeft += scrollOffset) : '';
  };

  useEffect(() => {
    if (checkOverflow(refLocations.current)) {
      setOverflowCampusActive(true);
    } else {
      setOverflowCampusActive(false);
    }
    if (checkOverflow(refAcommodations.current)) {
      setOverflowAcommodationsActive(true);
    } else {
      setOverflowAcommodationsActive(false);
    }
    if (checkOverflow(refLifePhotos.current)) {
      setOverflowLifePhotosActive(true);
    } else {
      setOverflowLifePhotosActive(false);
    }
  }, [
    overflowCampusActive,
    overflowAcommodationsActive,
    overflowLifePhotosActive,
    campusFound,
    entity,
  ]);

  const showCityAndCountryLifePhotos = useMemo(
    () =>
      (campusFound?.city.country.photos && campusFound?.city.country.photos.length > 0) ||
      (entityIsSchool(entity?.data) &&
        entity?.data.city.country.photos &&
        entity?.data.city.country.photos.length > 0) ||
      (entityIsProgram(entity?.data) &&
        entity?.data.school.city.country.photos &&
        entity?.data.school.city.country.photos.length > 0),
    [campusFound, entity]
  );

  const showStudyLifeText = useMemo(
    () =>
      campusFound?.city.study_life ||
      campusFound?.city.country.study_life ||
      (entityIsSchool(entity?.data) &&
        (entity?.data.city.study_life || entity?.data.city.country.study_life)) ||
      (entityIsProgram(entity?.data) &&
        (entity?.data.school.city.study_life || entity?.data.school.city.country.study_life)),
    [campusFound, entity]
  );

  const showCityAndCountryLife = useMemo(
    () => showCityAndCountryLifePhotos && showStudyLifeText,
    [campusFound, entity]
  );

  const studyLife = useMemo(
    () =>
      campusFound?.city.study_life
        ? campusFound.city.study_life
        : campusFound?.city.country.study_life
        ? campusFound.city.country.study_life
        : entityIsSchool(entity?.data) && entity?.data.city.study_life
        ? entity.data.city.study_life
        : entityIsSchool(entity?.data) && entity?.data.city.country.study_life
        ? entity.data.city.country.study_life
        : entityIsProgram(entity?.data) && entity?.data.school.city.study_life
        ? entity.data.school.city.study_life
        : entityIsProgram(entity?.data) && entity?.data.school.city.country.study_life
        ? entity.data.school.city.country.study_life
        : '',
    [campusFound, entity]
  );

  return (
    <div className={styles.sectionContainer} id="student-life" ref={studentLifeRef}>
      <div className={styles.sectionTitle}>
        <div className={styles.blueBlock} />
        <h2 className={styles.titleText}>Student life</h2>
      </div>
      <h3
        className={styles.subtitle}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: isMobile ? '0px' : '8px',
        }}
      >
        Campus location
        {!isMobile && overflowCampusActive && (
          <div className={styles.arrowsContainer}>
            <div className={styles.arrowContainer} onClick={() => scroll(-150, refLocations)}>
              <i className="bi bi-chevron-compact-left"></i>
            </div>
            <div className={styles.arrowContainer} onClick={() => scroll(150, refLocations)}>
              <i className="bi bi-chevron-compact-right"></i>
            </div>
          </div>
        )}
      </h3>
      {(entityIsProgram(entity?.data) &&
        entity?.data.school.campuses &&
        entity?.data.school.campuses.length > 0) ||
      (entityIsSchool(entity?.data) &&
        entity?.data.campuses &&
        entity?.data.campuses.length > 0) ? (
        <>
          <div
            className={`${styles.tagsContainer} row flex-row flex-nowrap mt-3 pt-1 gap-3`}
            ref={refLocations}
          >
            {entityIsSchool(entity?.data)
              ? entity?.data.campuses.map((campus: Campus) => (
                  <div className={styles.campus} key={campus.id}>
                    <CampusCard campus={campus} />
                  </div>
                ))
              : entity?.data.school.campuses.map((campus: Campus) => (
                  <div className={styles.campus} key={campus.id}>
                    <CampusCard campus={campus} />
                  </div>
                ))}
          </div>
          <div className={styles.subtitleContainer}>
            <h3 className={styles.subtitle} style={{ marginTop: 0 }}>
              Campus video tour
            </h3>
            <Select
              isSearchable={false}
              classNamePrefix="react-select"
              className={styles.campusSelector}
              placeholder="Select Campus"
              options={
                entityIsSchool(entity?.data)
                  ? entity?.data.campuses.map((c: Campus) => ({ label: c.name, value: c.id })) || []
                  : entity?.data.school.campuses.map((c: Campus) => ({
                      label: c.name,
                      value: c.id,
                    })) || []
              }
              onChange={(campus: any) => {
                setCampus(campus);
              }}
              value={campusSelected}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  background: '#F2F2F2',
                  color: '#19234F',
                  cursor: 'pointer',
                }),
              }}
            />
          </div>
          {campusFound?.video_link && (
            <iframe
              width="100%"
              height={isMobile ? '200' : '340'}
              src={campusFound?.video_link}
            ></iframe>
          )}
          {!!(
            (campusFound?.acommodation_photos && campusFound?.acommodation_photos?.length) ||
            campusFound?.acommodation_facilities
          ) && (
            <h3
              className={styles.subtitle}
              style={{
                marginTop: isMobile ? '27px' : '56px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              Acommodation & Facilities
              {!isMobile && overflowAcommodationsActive && (
                <div className={styles.arrowsContainer}>
                  <div
                    className={styles.arrowContainer}
                    onClick={() => scroll(-150, refAcommodations)}
                  >
                    <i className="bi bi-chevron-compact-left"></i>
                  </div>
                  <div
                    className={styles.arrowContainer}
                    onClick={() => scroll(150, refAcommodations)}
                  >
                    <i className="bi bi-chevron-compact-right"></i>
                  </div>
                </div>
              )}
            </h3>
          )}
          {!!(campusFound?.acommodation_photos && campusFound?.acommodation_photos?.length) && (
            <div
              className={`${styles.acommodationsContainer} row flex-row flex-nowrap mt-3 pt-1`}
              ref={refAcommodations}
            >
              {campusFound?.acommodation_photos?.map((photo) => (
                <div className={styles.acommodationContainer} key={photo}>
                  <img
                    alt={`Photo of ${campusSelected?.label}`}
                    className={styles.acommodationPhoto}
                    src={photo}
                  />
                </div>
              ))}
            </div>
          )}
          {campusFound?.acommodation_facilities && (
            <Text
              size={isMobile ? 14 : 16}
              weight={400}
              inlineStyle={{
                color: '#19234F',
                marginBottom: isMobile ? '27px' : '30px',
                marginTop: isMobile ? '16px' : '32px',
              }}
            >
              {campusFound?.acommodation_facilities}
            </Text>
          )}
        </>
      ) : (
        <div className={styles.mapContainer}>
          <img
            src={
              entityIsSchool(entity?.data)
                ? handleMap(entity?.data.name)
                : handleMap(entity?.data.school.name)
            }
            className={styles.mapContainer}
            alt="blogReview"
          />
        </div>
      )}
      {!!(
        (entityIsSchool(entity?.data) && entity?.data.student_support_description) ||
        (entityIsProgram(entity?.data) && entity?.data.school.student_support_description)
      ) && (
        <>
          <h3 className={styles.subtitle}>Student support</h3>
          <Text
            size={isMobile ? 14 : 16}
            weight={400}
            inlineStyle={{
              color: '#19234F',
              marginBottom: isMobile ? '27px' : '30px',
              marginTop: '16px',
            }}
          >
            {entityIsSchool(entity?.data)
              ? entity.data.student_support_description
              : entity.data.school.student_support_description}
          </Text>
        </>
      )}
      {showCityAndCountryLife && (
        <>
          <h3
            className={styles.subtitle}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            City and country life
            {!isMobile && overflowLifePhotosActive && (
              <div className={styles.arrowsContainer}>
                <div className={styles.arrowContainer} onClick={() => scroll(-150, refLifePhotos)}>
                  <i className="bi bi-chevron-compact-left"></i>
                </div>
                <div className={styles.arrowContainer} onClick={() => scroll(150, refLifePhotos)}>
                  <i className="bi bi-chevron-compact-right"></i>
                </div>
              </div>
            )}
          </h3>
          {showCityAndCountryLifePhotos && (
            <div
              className={`${styles.acommodationsContainer} row flex-row flex-nowrap mt-3 pt-1`}
              ref={refLifePhotos}
            >
              {campusFound?.city.country.photos && campusFound?.city.country.photos.length > 0
                ? campusFound?.city.country.photos?.map((photo) => (
                    <div className={styles.acommodationContainer} key={photo}>
                      <img
                        alt={`Photo of ${campusSelected?.label}`}
                        className={styles.acommodationPhoto}
                        src={photo}
                      />
                    </div>
                  ))
                : entityIsSchool(entity?.data)
                ? entity?.data.city.country.photos?.map((photo) => (
                    <div className={styles.acommodationContainer} key={photo}>
                      <img
                        alt={`Photo of ${campusSelected?.label}`}
                        className={styles.acommodationPhoto}
                        src={photo}
                      />
                    </div>
                  ))
                : entity?.data.school.city.country.photos?.map((photo) => (
                    <div className={styles.acommodationContainer} key={photo}>
                      <img
                        alt={`Photo of ${campusSelected?.label}`}
                        className={styles.acommodationPhoto}
                        src={photo}
                      />
                    </div>
                  ))}
            </div>
          )}
          {showStudyLifeText && (
            <Text
              size={isMobile ? 14 : 16}
              weight={400}
              inlineStyle={{
                color: '#19234F',
                marginBottom: '0px',
                marginTop: '16px',
              }}
            >
              <ReactMarkdown rehypePlugins={[rehypeRaw]}>{studyLife}</ReactMarkdown>
            </Text>
          )}
        </>
      )}
      <div className={styles.countryDetailButton} onClick={handleCountryDetailButton}>
        <Text
          size={isMobile ? 14 : 16}
          weight={600}
          inlineStyle={{
            color: '#19234F',
          }}
        >
          More about this country
        </Text>
        <i className={`bi bi-chevron-right ${styles.arrow}`}></i>
      </div>
    </div>
  );
};

export default StudentLife;
